import React from 'react';

interface CustomRoom {
  id: string;
  name: string;
  count: number;
}

interface FloorPlanGeneratorProps {
  config: {
    bedrooms: number;
    bathrooms: number;
    livingRooms: number;
    customRooms: CustomRoom[];
    area: number;
    unit: string;
    kitchenType: string;
    extras: string[];
    prompt: string;
  };
  onChange: (config: any) => void;
  darkMode: boolean;
  onReset?: () => void;
  onGenerate?: () => void;
  isGenerating?: boolean;
}

const EXTRAS = [
  { label: 'Garage', icon: 'ri-car-line' },
  { label: 'Pool', icon: 'ri-water-flash-line' },
  { label: 'Gym', icon: 'ri-run-line' },
  { label: 'Home Office', icon: 'ri-computer-line' },
  { label: 'Balcony', icon: 'ri-building-line' },
  { label: 'Terrace', icon: 'ri-sun-line' },
  { label: 'Garden', icon: 'ri-plant-line' },
  { label: 'Basement', icon: 'ri-arrow-down-line' },
  { label: 'Attic', icon: 'ri-arrow-up-line' },
  { label: 'Laundry Room', icon: 'ri-t-shirt-line' },
];

const KITCHEN_TYPES = [
  { value: 'open', label: 'Open', icon: 'ri-layout-masonry-line', desc: 'Merged with living' },
  { value: 'closed', label: 'Closed', icon: 'ri-layout-column-line', desc: 'Separate room' },
  { value: 'galley', label: 'Galley', icon: 'ri-layout-row-line', desc: 'Corridor style' },
  { value: 'island', label: 'Island', icon: 'ri-layout-grid-line', desc: 'Center island' },
];

const SAVED_TEMPLATES_KEY = 'masterArchitectCustomTemplates';

export default function FloorPlanGenerator({ config, onChange, darkMode, onReset, onGenerate, isGenerating }: FloorPlanGeneratorProps) {
  const [showCalculator, setShowCalculator] = React.useState(false);
  const [roomDimensions, setRoomDimensions] = React.useState<any>(null);
  const [attachedFiles, setAttachedFiles] = React.useState<Array<{ name: string; size: number; type: string; url: string }>>([]);
  const [showAttachments, setShowAttachments] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const [customRoomInput, setCustomRoomInput] = React.useState('');
  const [customRoomError, setCustomRoomError] = React.useState('');
  const [showSaveTemplateModal, setShowSaveTemplateModal] = React.useState(false);
  const [templateNameInput, setTemplateNameInput] = React.useState('');
  const [templateSaveError, setTemplateSaveError] = React.useState('');
  const [templateSavedToast, setTemplateSavedToast] = React.useState('');
  const [savedCustomTemplates, setSavedCustomTemplates] = React.useState<Array<{
    id: string; name: string; icon: string; desc: string;
    config: { livingRooms: number; bedrooms: number; bathrooms: number; customRooms: CustomRoom[] };
  }>>(() => {
    try { return JSON.parse(localStorage.getItem(SAVED_TEMPLATES_KEY) || '[]'); } catch { return []; }
  });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const bg = darkMode ? 'bg-gray-800' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const cardBg = darkMode ? 'bg-gray-700/60 border-gray-600' : 'bg-gray-50 border-gray-200';

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
  const MAX_FILES = 10;
  const ACCEPTED_FORMATS = {
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    video: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'],
    document: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/csv'],
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles: Array<{ name: string; size: number; type: string; url: string }> = [];
    let errorMsg = '';

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Check total file count
      if (attachedFiles.length + newFiles.length >= MAX_FILES) {
        errorMsg = `Maximum ${MAX_FILES} files allowed`;
        break;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errorMsg = `${file.name} exceeds 25MB limit`;
        continue;
      }

      // Check file type
      const allAcceptedTypes = [...ACCEPTED_FORMATS.image, ...ACCEPTED_FORMATS.video, ...ACCEPTED_FORMATS.document];
      if (!allAcceptedTypes.includes(file.type)) {
        errorMsg = `${file.name} format not supported`;
        continue;
      }

      // Create file object
      const fileUrl = URL.createObjectURL(file);
      newFiles.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: fileUrl,
      });
    }

    if (newFiles.length > 0) {
      setAttachedFiles([...attachedFiles, ...newFiles]);
      setUploadError('');
    }

    if (errorMsg) {
      setUploadError(errorMsg);
      setTimeout(() => setUploadError(''), 4000);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = attachedFiles.filter((_, i) => i !== index);
    setAttachedFiles(newFiles);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return 'ri-image-line';
    if (type.startsWith('video/')) return 'ri-video-line';
    if (type === 'application/pdf') return 'ri-file-pdf-line';
    if (type.includes('word')) return 'ri-file-word-line';
    return 'ri-file-line';
  };

  const isImageFile = (type: string) => type.startsWith('image/');

  const handleAddCustomRoom = () => {
    const name = customRoomInput.trim();
    if (!name) return;
    const duplicate = (config.customRooms || []).some(
      (r: CustomRoom) => r.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      setCustomRoomError('Room already added');
      setTimeout(() => setCustomRoomError(''), 2500);
      return;
    }
    const newRoom: CustomRoom = { id: Date.now().toString(), name, count: 1 };
    onChange({ ...config, customRooms: [...(config.customRooms || []), newRoom] });
    setCustomRoomInput('');
    setCustomRoomError('');
  };

  const handleCustomRoomCount = (id: string, delta: number) => {
    const updated = (config.customRooms || []).map((r: CustomRoom) =>
      r.id === id ? { ...r, count: Math.max(1, Math.min(10, r.count + delta)) } : r
    );
    onChange({ ...config, customRooms: updated });
  };

  const handleRemoveCustomRoom = (id: string) => {
    onChange({ ...config, customRooms: (config.customRooms || []).filter((r: CustomRoom) => r.id !== id) });
  };

  const handleCustomRoomKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddCustomRoom(); }
  };

  // Build room breakdown for bar chart
  const buildRoomBreakdown = () => {
    const rooms: { label: string; count: number; color: string; icon: string }[] = [
      { label: 'Living', count: config.livingRooms || 1, color: '#14B8A6', icon: 'ri-sofa-line' },
      { label: 'Bedroom', count: config.bedrooms, color: '#8B5CF6', icon: 'ri-hotel-bed-line' },
      { label: 'Bathroom', count: config.bathrooms, color: '#06B6D4', icon: 'ri-water-flash-line' },
      { label: 'Kitchen', count: 1, color: '#F59E0B', icon: 'ri-restaurant-line' },
    ];
    (config.customRooms || []).forEach((r: CustomRoom) => {
      rooms.push({ label: r.name, count: r.count, color: '#EC4899', icon: 'ri-home-gear-line' });
    });
    if (config.extras && config.extras.length > 0) {
      rooms.push({ label: 'Extras', count: config.extras.length, color: '#6B7280', icon: 'ri-add-circle-line' });
    }
    return rooms;
  };

  const roomBreakdown = buildRoomBreakdown();
  const totalRoomCount = roomBreakdown.reduce((s, r) => s + r.count, 0);

  // Room Templates presets
  const ROOM_TEMPLATES = [
    {
      id: 'starter',
      name: 'Starter Home',
      icon: 'ri-home-line',
      desc: 'Cozy 2-bed, 1-bath',
      config: { livingRooms: 1, bedrooms: 2, bathrooms: 1, customRooms: [] },
    },
    {
      id: 'family',
      name: 'Family Home',
      icon: 'ri-home-smile-line',
      desc: '4-bed, 2-bath with family room',
      config: { livingRooms: 2, bedrooms: 4, bathrooms: 2, customRooms: [] },
    },
    {
      id: 'luxury',
      name: 'Luxury Villa',
      icon: 'ri-vip-crown-line',
      desc: '6-bed, 4-bath + extras',
      config: { livingRooms: 3, bedrooms: 6, bathrooms: 4, customRooms: [{ id: 'theater', name: 'Home Theater', count: 1 }, { id: 'gym', name: 'Gym', count: 1 }] },
    },
    {
      id: 'modern',
      name: 'Modern Loft',
      icon: 'ri-building-4-line',
      desc: 'Open 1-bed, 1-bath + studio',
      config: { livingRooms: 1, bedrooms: 1, bathrooms: 1, customRooms: [{ id: 'studio', name: 'Studio', count: 1 }] },
    },
  ];

  const applyTemplate = (tpl: { config: { livingRooms: number; bedrooms: number; bathrooms: number; customRooms: CustomRoom[] } }) => {
    onChange({
      ...config,
      livingRooms: tpl.config.livingRooms,
      bedrooms: tpl.config.bedrooms,
      bathrooms: tpl.config.bathrooms,
      customRooms: tpl.config.customRooms.map(r => ({ ...r, id: Date.now().toString() + r.id })),
    });
  };

  const handleSaveTemplate = () => {
    const name = templateNameInput.trim();
    if (!name) { setTemplateSaveError('Please enter a template name'); return; }
    const duplicate = savedCustomTemplates.some(t => t.name.toLowerCase() === name.toLowerCase());
    if (duplicate) { setTemplateSaveError('A template with this name already exists'); return; }
    const newTemplate = {
      id: Date.now().toString(),
      name,
      icon: 'ri-bookmark-line',
      desc: `${config.bedrooms} bed · ${config.bathrooms} bath · ${config.livingRooms || 1} living`,
      config: {
        livingRooms: config.livingRooms || 1,
        bedrooms: config.bedrooms,
        bathrooms: config.bathrooms,
        customRooms: (config.customRooms || []).map(r => ({ ...r })),
      },
    };
    const updated = [...savedCustomTemplates, newTemplate];
    setSavedCustomTemplates(updated);
    localStorage.setItem(SAVED_TEMPLATES_KEY, JSON.stringify(updated));
    setShowSaveTemplateModal(false);
    setTemplateNameInput('');
    setTemplateSaveError('');
    setTemplateSavedToast(`"${name}" saved!`);
    setTimeout(() => setTemplateSavedToast(''), 2500);
  };

  const handleDeleteCustomTemplate = (id: string) => {
    const updated = savedCustomTemplates.filter(t => t.id !== id);
    setSavedCustomTemplates(updated);
    localStorage.setItem(SAVED_TEMPLATES_KEY, JSON.stringify(updated));
  };

  const calculateOptimalRooms = () => {
    const totalArea = config.area;
    const unit = config.unit;
    const bedrooms = config.bedrooms;
    const bathrooms = config.bathrooms;

    // Base allocation percentages
    const livingRoomPercent = 0.20;
    const masterBedroomPercent = 0.15;
    const secondaryBedroomPercent = 0.10;
    const bathroomPercent = 0.05;
    const kitchenPercent = 0.12;

    // Calculate areas
    const livingRoom = totalArea * livingRoomPercent;
    const masterBedroom = totalArea * masterBedroomPercent;
    const secondaryBedroom = totalArea * secondaryBedroomPercent;
    const bathroom = totalArea * bathroomPercent;
    const kitchen = totalArea * kitchenPercent;

    // Build room list
    const rooms = [
      { name: 'Living Room', area: livingRoom, type: 'living' },
      { name: 'Master Bedroom', area: masterBedroom, type: 'bedroom' },
      { name: 'Kitchen', area: kitchen, type: 'kitchen' },
    ];

    // Add secondary bedrooms
    for (let i = 2; i <= bedrooms; i++) {
      rooms.push({ name: `Bedroom ${i}`, area: secondaryBedroom, type: 'bedroom' });
    }

    // Add bathrooms
    for (let i = 1; i <= bathrooms; i++) {
      rooms.push({ name: `Bathroom ${i}`, area: bathroom, type: 'bathroom' });
    }

    // Calculate dimensions (assuming roughly square rooms)
    const roomsWithDimensions = rooms.map(room => {
      const sqrtArea = Math.sqrt(room.area);
      const width = Math.round(sqrtArea);
      const length = Math.round(room.area / width);
      return {
        ...room,
        width,
        length,
        dimensions: `${width} × ${length} ${unit === 'sqft' ? 'ft' : 'm'}`,
        areaDisplay: Math.round(room.area),
      };
    });

    // Calculate total allocated area
    const allocatedArea = rooms.reduce((sum, room) => sum + room.area, 0);
    const hallwayArea = totalArea - allocatedArea;
    const utilizationPercent = (allocatedArea / totalArea) * 100;

    // Determine utilization status
    let utilizationStatus = 'good';
    let utilizationColor = 'green';
    if (utilizationPercent > 85) {
      utilizationStatus = 'tight';
      utilizationColor = 'amber';
    }
    if (allocatedArea > totalArea || roomsWithDimensions.some(r => r.areaDisplay < 50)) {
      utilizationStatus = 'too-small';
      utilizationColor = 'red';
    }

    setRoomDimensions({
      rooms: roomsWithDimensions,
      hallwayArea: Math.round(hallwayArea),
      utilizationPercent: Math.round(utilizationPercent),
      utilizationStatus,
      utilizationColor,
      totalAllocated: Math.round(allocatedArea),
    });
    setShowCalculator(true);
  };

  const CounterButton = ({ value, min, max, onChange: onCountChange }: { value: number; min: number; max: number; onChange: (v: number) => void }) => (
    <div className={`flex items-center gap-3 rounded-xl border px-4 py-2 ${cardBg}`}>
      <button
        onClick={() => onCountChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-lg transition-all cursor-pointer ${
          value <= min
            ? darkMode ? 'bg-gray-600 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : darkMode ? 'bg-gray-600 hover:bg-teal-600 text-white' : 'bg-white hover:bg-teal-500 hover:text-white text-gray-700 border border-gray-200'
        }`}
      >
        <i className="ri-subtract-line text-sm"></i>
      </button>
      <span className={`text-2xl font-bold w-8 text-center ${text}`}>{value}</span>
      <button
        onClick={() => onCountChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-lg transition-all cursor-pointer ${
          value >= max
            ? darkMode ? 'bg-gray-600 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-teal-500 hover:bg-teal-600 text-white'
        }`}
      >
        <i className="ri-add-line text-sm"></i>
      </button>
    </div>
  );

  return (
    <div className="p-5 space-y-5">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm,.pdf,.docx,.txt,.csv"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {/* Header summary card — with proportional bar chart */}
      <div className={`rounded-xl border p-4 ${darkMode ? 'bg-gradient-to-br from-teal-900/30 to-cyan-900/20 border-teal-700/50' : 'bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200'}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500">
            <i className="ri-layout-grid-line text-white text-sm"></i>
          </div>
          <div>
            <h3 className={`text-sm font-bold ${text}`}>Floor Plan Configuration</h3>
            <p className={`text-xs ${subtext}`}>Customize your dream home layout</p>
          </div>
        </div>

        {/* Pill badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { icon: 'ri-hotel-bed-line', val: `${config.bedrooms} Bed` },
            { icon: 'ri-water-flash-line', val: `${config.bathrooms} Bath` },
            { icon: 'ri-sofa-line', val: `${config.livingRooms || 1} Living` },
            { icon: 'ri-ruler-line', val: `${config.area} ${config.unit}` },
            { icon: 'ri-restaurant-line', val: `${config.kitchenType.charAt(0).toUpperCase() + config.kitchenType.slice(1)} Kitchen` },
          ].map((item) => (
            <span key={item.val} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-white text-teal-700 border border-teal-200'}`}>
              <i className={`${item.icon} text-teal-500`}></i>
              {item.val}
            </span>
          ))}
          {(config.customRooms || []).map((r: CustomRoom) => (
            <span key={r.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-pink-900/30 text-pink-300' : 'bg-white text-pink-700 border border-pink-200'}`}>
              <i className="ri-home-gear-line text-pink-500"></i>
              {r.count > 1 ? `${r.count}× ` : ''}{r.name}
            </span>
          ))}
          {config.extras.length > 0 && (
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-600 border border-gray-200'}`}>
              <i className="ri-add-circle-line text-gray-400"></i>
              {config.extras.length} extra{config.extras.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Proportional bar chart */}
        <div>
          <p className={`text-xs font-semibold mb-2 ${subtext}`}>Room Breakdown — {totalRoomCount} total spaces</p>
          {/* Segmented bar */}
          <div className="flex h-4 rounded-full overflow-hidden gap-px mb-2">
            {roomBreakdown.map((r) => (
              <div
                key={r.label}
                className="transition-all duration-500 first:rounded-l-full last:rounded-r-full"
                style={{
                  width: `${(r.count / totalRoomCount) * 100}%`,
                  backgroundColor: r.color,
                  minWidth: r.count > 0 ? '4px' : '0',
                }}
                title={`${r.label}: ${r.count}`}
              />
            ))}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {roomBreakdown.map((r) => (
              <div key={r.label} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: r.color }} />
                <span className={`text-xs ${subtext}`}>
                  {r.label} <span className="font-semibold" style={{ color: r.color }}>{r.count}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Room Templates ── */}
      <div className={`rounded-xl border p-4 ${darkMode ? 'bg-indigo-900/20 border-indigo-700/50' : 'bg-indigo-50/60 border-indigo-200'}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${darkMode ? 'bg-indigo-900/40' : 'bg-indigo-100'}`}>
            <i className={`ri-layout-masonry-line text-sm ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
          </div>
          <div className="flex-1">
            <label className={`block text-sm font-bold ${text}`}>Room Templates</label>
            <p className={`text-xs ${subtext}`}>One-click presets for common layouts</p>
          </div>
          <button
            onClick={() => { setTemplateNameInput(''); setTemplateSaveError(''); setShowSaveTemplateModal(true); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              darkMode ? 'bg-indigo-800/60 text-indigo-300 hover:bg-indigo-700/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            }`}
          >
            <i className="ri-bookmark-add-line"></i>
            Save Current
          </button>
        </div>

        {/* Built-in templates */}
        <div className="grid grid-cols-2 gap-2">
          {ROOM_TEMPLATES.map((t) => (
            <button
              key={t.id}
              onClick={() => applyTemplate(t)}
              className={`p-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                darkMode
                  ? 'border-gray-600 bg-gray-700/60 hover:border-indigo-500 hover:bg-indigo-900/30'
                  : 'border-gray-200 bg-white hover:border-indigo-400 hover:bg-indigo-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-6 h-6 flex items-center justify-center rounded-md ${darkMode ? 'bg-indigo-900/40' : 'bg-indigo-100'}`}>
                  <i className={`${t.icon} text-xs ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                </div>
                <span className={`text-xs font-bold ${text}`}>{t.name}</span>
              </div>
              <p className={`text-xs ${subtext} leading-tight`}>{t.desc}</p>
            </button>
          ))}
        </div>

        {/* Custom saved templates */}
        {savedCustomTemplates.length > 0 && (
          <div className="mt-3">
            <p className={`text-xs font-semibold mb-2 ${subtext} flex items-center gap-1.5`}>
              <i className="ri-bookmark-fill text-indigo-400"></i>
              My Saved Templates ({savedCustomTemplates.length})
            </p>
            <div className="space-y-1.5">
              {savedCustomTemplates.map((t) => (
                <div
                  key={t.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700/60 border-gray-600' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className={`w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0 ${darkMode ? 'bg-indigo-900/40' : 'bg-indigo-100'}`}>
                    <i className={`ri-bookmark-fill text-xs ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold truncate ${text}`}>{t.name}</p>
                    <p className={`text-xs ${subtext}`}>{t.desc}</p>
                  </div>
                  <button
                    onClick={() => applyTemplate(t)}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      darkMode ? 'bg-indigo-800/60 text-indigo-300 hover:bg-indigo-700' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                    }`}
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => handleDeleteCustomTemplate(t.id)}
                    className={`w-6 h-6 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                      darkMode ? 'text-gray-500 hover:text-rose-400 hover:bg-gray-600' : 'text-gray-400 hover:text-rose-500 hover:bg-rose-50'
                    }`}
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Saved toast */}
        {templateSavedToast && (
          <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${darkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700'}`}>
            <i className="ri-checkbox-circle-fill text-teal-500"></i>
            {templateSavedToast}
          </div>
        )}
      </div>

      {/* ── Save Template Modal ── */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl shadow-2xl w-full max-w-sm p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${darkMode ? 'bg-indigo-900/40' : 'bg-indigo-100'}`}>
                  <i className={`ri-bookmark-add-line text-lg ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}></i>
                </div>
                <div>
                  <h3 className={`text-sm font-bold ${text}`}>Save as Template</h3>
                  <p className={`text-xs ${subtext}`}>Name your current room config</p>
                </div>
              </div>
              <button onClick={() => setShowSaveTemplateModal(false)} className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}>
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Current config preview */}
            <div className={`rounded-xl p-3 mb-4 ${darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}`}>
              <p className={`text-xs font-semibold mb-2 ${subtext}`}>Will save:</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  `${config.livingRooms || 1} Living`,
                  `${config.bedrooms} Bed`,
                  `${config.bathrooms} Bath`,
                  ...(config.customRooms || []).map(r => `${r.count > 1 ? r.count + '× ' : ''}${r.name}`),
                ].map((item) => (
                  <span key={item} className={`px-2 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <input
              type="text"
              value={templateNameInput}
              onChange={(e) => { setTemplateNameInput(e.target.value); setTemplateSaveError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTemplate(); }}
              placeholder="e.g. My Beach House, Client A Layout…"
              maxLength={40}
              autoFocus
              className={`w-full px-4 py-2.5 rounded-xl border text-sm mb-2 transition-colors ${
                templateSaveError ? 'border-rose-400 focus:ring-rose-400' : ''
              } ${inputBg} focus:ring-2 focus:border-transparent`}
            />
            {templateSaveError && (
              <p className="text-xs text-rose-500 mb-3 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>{templateSaveError}
              </p>
            )}

            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowSaveTemplateModal(false)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!templateNameInput.trim()}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
                  templateNameInput.trim()
                    ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-600 hover:to-indigo-700'
                    : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-bookmark-fill mr-1.5"></i>Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Design Prompt ── */}
      <div className={`rounded-xl border p-4 ${darkMode ? 'bg-gray-700/40 border-gray-600' : 'bg-amber-50/60 border-amber-200'}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${darkMode ? 'bg-amber-900/40' : 'bg-amber-100'}`}>
            <i className={`ri-quill-pen-line text-sm ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}></i>
          </div>
          <div>
            <label className={`block text-sm font-bold ${text}`}>
              Describe Your Design
            </label>
            <p className={`text-xs ${subtext}`}>Tell the AI your custom requirements</p>
          </div>
          <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
            Optional
          </span>
        </div>

        <textarea
          value={config.prompt}
          onChange={(e) => {
            if (e.target.value.length <= 500) {
              onChange({ ...config, prompt: e.target.value });
            }
          }}
          placeholder="e.g. Open-plan living area with floor-to-ceiling windows facing south, a double garage on the left side, wraparound porch, vaulted ceilings in the master bedroom, and a mudroom near the entrance..."
          rows={4}
          className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors resize-none placeholder-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent ${inputBg}`}
        />

        {/* Character counter + quick chips */}
        <div className="flex items-center justify-between mt-2 mb-3">
          <div className="flex flex-wrap gap-1.5">
            {['South-facing garden', 'Open-plan living', 'Double garage', 'Vaulted ceilings', 'Wraparound porch'].map((chip) => (
              <button
                key={chip}
                onClick={() => {
                  const separator = config.prompt && !config.prompt.endsWith(' ') ? ', ' : '';
                  const newPrompt = (config.prompt + separator + chip).slice(0, 500);
                  onChange({ ...config, prompt: newPrompt });
                }}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  config.prompt.includes(chip)
                    ? darkMode ? 'bg-amber-700/50 text-amber-300' : 'bg-amber-200 text-amber-800'
                    : darkMode ? 'bg-gray-600 text-gray-300 hover:bg-amber-800/40 hover:text-amber-300' : 'bg-white text-gray-600 border border-gray-200 hover:border-amber-300 hover:text-amber-700'
                }`}
              >
                + {chip}
              </button>
            ))}
          </div>
          <span className={`text-xs font-semibold flex-shrink-0 ml-2 ${
            config.prompt.length > 450
              ? 'text-rose-500'
              : config.prompt.length > 300
              ? darkMode ? 'text-amber-400' : 'text-amber-600'
              : subtext
          }`}>
            {config.prompt.length}/500
          </span>
        </div>

        {config.prompt.length > 0 && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${darkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700'}`}>
            <i className="ri-checkbox-circle-fill text-teal-500"></i>
            Prompt will be used to personalize your generated floor plan
          </div>
        )}
      </div>

      {/* ── Reference Files Attachment Section ── */}
      <div className={`rounded-xl border p-4 ${darkMode ? 'bg-purple-900/20 border-purple-700/50' : 'bg-purple-50/60 border-purple-200'}`}>
        <button
          onClick={() => setShowAttachments(!showAttachments)}
          className="w-full flex items-center justify-between cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${darkMode ? 'bg-purple-900/40' : 'bg-purple-100'}`}>
              <i className={`ri-attachment-2 text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}></i>
            </div>
            <div className="text-left">
              <h3 className={`text-sm font-bold ${text}`}>Reference Files</h3>
              <p className={`text-xs ${subtext}`}>Attach images, videos, or documents</p>
            </div>
            {attachedFiles.length > 0 && (
              <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full font-bold">
                {attachedFiles.length}
              </span>
            )}
          </div>
          <i className={`ri-arrow-${showAttachments ? 'up' : 'down'}-s-line text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
        </button>

        {showAttachments && (
          <div className="mt-4 space-y-3">
            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                isDragging
                  ? darkMode
                    ? 'border-purple-500 bg-purple-900/30'
                    : 'border-purple-400 bg-purple-100'
                  : darkMode
                  ? 'border-gray-600 bg-gray-800/40 hover:border-purple-600 hover:bg-purple-900/20'
                  : 'border-gray-300 bg-white hover:border-purple-300 hover:bg-purple-50'
              }`}
            >
              <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${darkMode ? 'bg-purple-900/40' : 'bg-purple-100'}`}>
                <i className={`ri-upload-cloud-2-line text-2xl ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}></i>
              </div>
              <p className={`text-sm font-semibold mb-1 ${text}`}>
                {isDragging ? 'Drop files here' : 'Click to browse or drag files here'}
              </p>
              <p className={`text-xs ${subtext}`}>
                Images, Videos, PDFs, Documents • Max 25MB • Up to {MAX_FILES} files
              </p>
            </div>

            {/* Upload Error */}
            {uploadError && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${darkMode ? 'bg-red-900/30 text-red-300 border border-red-700' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                <i className="ri-error-warning-line"></i>
                {uploadError}
              </div>
            )}

            {/* Attached Files Display */}
            {attachedFiles.length > 0 && (
              <div className="space-y-3">
                {/* Image Previews */}
                {attachedFiles.some(f => isImageFile(f.type)) && (
                  <div>
                    <p className={`text-xs font-semibold mb-2 ${text}`}>Images ({attachedFiles.filter(f => isImageFile(f.type)).length})</p>
                    <div className="grid grid-cols-4 gap-2">
                      {attachedFiles.filter(f => isImageFile(f.type)).map((file, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={file.url}
                            alt={file.name}
                            className="w-full h-20 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                          />
                          <button
                            onClick={() => handleRemoveFile(attachedFiles.indexOf(file))}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video & Document Files */}
                {attachedFiles.some(f => !isImageFile(f.type)) && (
                  <div>
                    <p className={`text-xs font-semibold mb-2 ${text}`}>Files ({attachedFiles.filter(f => !isImageFile(f.type)).length})</p>
                    <div className="space-y-2">
                      {attachedFiles.filter(f => !isImageFile(f.type)).map((file, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}
                        >
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <i className={`${getFileIcon(file.type)} text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-semibold truncate ${text}`}>{file.name}</p>
                            <p className={`text-xs ${subtext}`}>{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveFile(attachedFiles.indexOf(file))}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Living Rooms */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className={`text-sm font-semibold ${text} flex items-center gap-2`}>
            <i className="ri-sofa-line text-teal-500"></i>
            Living / Lounge Rooms
          </label>
          <span className={`text-xs ${subtext}`}>1 – 6</span>
        </div>
        <CounterButton
          value={config.livingRooms || 1}
          min={1}
          max={6}
          onChange={(v) => onChange({ ...config, livingRooms: v })}
        />
      </div>

      {/* Bedrooms */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className={`text-sm font-semibold ${text} flex items-center gap-2`}>
            <i className="ri-hotel-bed-line text-teal-500"></i>
            Bedrooms
          </label>
          <span className={`text-xs ${subtext}`}>1 – 12</span>
        </div>
        <CounterButton
          value={config.bedrooms}
          min={1}
          max={12}
          onChange={(v) => onChange({ ...config, bedrooms: v })}
        />
      </div>

      {/* Bathrooms */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className={`text-sm font-semibold ${text} flex items-center gap-2`}>
            <i className="ri-water-flash-line text-teal-500"></i>
            Bathrooms
          </label>
          <span className={`text-xs ${subtext}`}>1 – 10</span>
        </div>
        <CounterButton
          value={config.bathrooms}
          min={1}
          max={10}
          onChange={(v) => onChange({ ...config, bathrooms: v })}
        />
      </div>

      {/* Custom Rooms */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className={`text-sm font-semibold ${text} flex items-center gap-2`}>
            <i className="ri-home-gear-line text-pink-500"></i>
            Custom Rooms
            {(config.customRooms || []).length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-pink-500 text-white text-xs rounded-full font-bold">
                {(config.customRooms || []).length}
              </span>
            )}
          </label>
          <span className={`text-xs ${subtext}`}>Home Theater, Wine Cellar…</span>
        </div>

        {/* Quick-add chips */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {['Home Theater', 'Wine Cellar', 'Gym', 'Library', 'Playroom', 'Studio', 'Sauna', 'Mudroom'].filter(
            (name) => !(config.customRooms || []).some((r: CustomRoom) => r.name === name)
          ).map((name) => (
            <button
              key={name}
              onClick={() => {
                const newRoom: CustomRoom = { id: Date.now().toString() + name, name, count: 1 };
                onChange({ ...config, customRooms: [...(config.customRooms || []), newRoom] });
              }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-pink-500 hover:text-pink-300'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-pink-400 hover:text-pink-700'
              }`}
            >
              <i className="ri-add-line mr-1"></i>{name}
            </button>
          ))}
        </div>

        {/* Custom input */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={customRoomInput}
            onChange={(e) => setCustomRoomInput(e.target.value)}
            onKeyDown={handleCustomRoomKeyDown}
            placeholder="Type a room name…"
            maxLength={40}
            className={`flex-1 px-3 py-2 rounded-xl border text-sm transition-colors ${inputBg} focus:ring-2 focus:ring-pink-400 focus:border-transparent`}
          />
          <button
            onClick={handleAddCustomRoom}
            disabled={!customRoomInput.trim()}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              customRoomInput.trim()
                ? 'bg-pink-500 hover:bg-pink-600 text-white'
                : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <i className="ri-add-line"></i>
          </button>
        </div>

        {customRoomError && (
          <p className="text-xs text-rose-500 mb-2 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>{customRoomError}
          </p>
        )}

        {/* Added custom rooms list */}
        {(config.customRooms || []).length > 0 && (
          <div className="space-y-2">
            {(config.customRooms || []).map((room: CustomRoom) => (
              <div
                key={room.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                  darkMode ? 'bg-gray-700/60 border-gray-600' : 'bg-pink-50/60 border-pink-200'
                }`}
              >
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${darkMode ? 'bg-pink-900/40' : 'bg-pink-100'}`}>
                  <i className={`ri-home-gear-line text-sm ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}></i>
                </div>
                <span className={`flex-1 text-sm font-medium truncate ${text}`}>{room.name}</span>
                {/* Mini counter */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleCustomRoomCount(room.id, -1)}
                    disabled={room.count <= 1}
                    className={`w-6 h-6 rounded-md flex items-center justify-center text-xs transition-all cursor-pointer ${
                      room.count <= 1
                        ? darkMode ? 'bg-gray-600 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : darkMode ? 'bg-gray-600 hover:bg-pink-700 text-white' : 'bg-white hover:bg-pink-500 hover:text-white text-gray-700 border border-gray-200'
                    }`}
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className={`w-5 text-center text-sm font-bold ${text}`}>{room.count}</span>
                  <button
                    onClick={() => handleCustomRoomCount(room.id, 1)}
                    disabled={room.count >= 10}
                    className={`w-6 h-6 rounded-md flex items-center justify-center text-xs transition-all cursor-pointer ${
                      room.count >= 10
                        ? darkMode ? 'bg-gray-600 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-pink-500 hover:bg-pink-600 text-white'
                    }`}
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <button
                  onClick={() => handleRemoveCustomRoom(room.id)}
                  className={`w-6 h-6 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                    darkMode ? 'text-gray-400 hover:text-rose-400 hover:bg-gray-600' : 'text-gray-400 hover:text-rose-500 hover:bg-rose-50'
                  }`}
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Area */}
      <div>
        <label className={`block text-sm font-semibold mb-3 ${text} flex items-center gap-2`}>
          <i className="ri-ruler-line text-teal-500"></i>
          Total Area
        </label>
        <div className="flex gap-3">
          <input
            type="number"
            value={config.area}
            onChange={(e) => onChange({ ...config, area: parseInt(e.target.value) || 0 })}
            className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${inputBg} focus:ring-2 focus:ring-teal-500 focus:border-transparent`}
          />
          <select
            value={config.unit}
            onChange={(e) => onChange({ ...config, unit: e.target.value })}
            className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${inputBg} focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer`}
          >
            <option value="sqft">sq ft</option>
            <option value="sqm">sq m</option>
          </select>
        </div>
        {/* Area visual bar */}
        <div className="mt-3">
          <div className={`w-full h-1.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((config.area / 5000) * 100, 100)}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className={`text-xs ${subtext}`}>500</span>
            <span className={`text-xs font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>{config.area} {config.unit}</span>
            <span className={`text-xs ${subtext}`}>5000+</span>
          </div>
        </div>

        {/* Calculate Optimal Rooms Button */}
        <button
          onClick={calculateOptimalRooms}
          className={`w-full mt-3 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer border ${
            darkMode
              ? 'border-indigo-600 bg-indigo-900/30 text-indigo-300 hover:bg-indigo-900/50'
              : 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
          }`}
        >
          <i className="ri-calculator-line"></i>
          Calculate Optimal Rooms
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>

      {/* Room Dimension Calculator Results */}
      {showCalculator && roomDimensions && (
        <div className={`rounded-xl border p-4 space-y-4 ${darkMode ? 'bg-indigo-900/20 border-indigo-700/50' : 'bg-indigo-50 border-indigo-200'}`}>
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500">
                <i className="ri-calculator-line text-white text-lg"></i>
              </div>
              <div>
                <h3 className={`text-sm font-bold ${text} flex items-center gap-2`}>
                  Optimal Room Dimensions
                  <button
                    className={`w-4 h-4 flex items-center justify-center rounded-full ${darkMode ? 'bg-indigo-800 text-indigo-300' : 'bg-indigo-200 text-indigo-600'} cursor-pointer group relative`}
                  >
                    <i className="ri-information-line text-xs"></i>
                    <div className={`absolute left-0 top-6 w-64 p-3 rounded-lg shadow-lg text-xs leading-relaxed opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-10 ${darkMode ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-white text-gray-700 border border-gray-200'}`}>
                      Dimensions calculated using proportional area distribution: Living 20%, Master 15%, Bedrooms 10%, Bathrooms 5%, Kitchen 12%, remainder for hallways.
                    </div>
                  </button>
                </h3>
                <p className={`text-xs ${subtext}`}>Based on {config.area} {config.unit} total area</p>
              </div>
            </div>
            <button
              onClick={() => setShowCalculator(false)}
              className={`w-6 h-6 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>

          {/* Space Utilization Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-semibold ${text}`}>Space Utilization</span>
              <span className={`text-xs font-bold ${
                roomDimensions.utilizationColor === 'green' ? 'text-green-500' :
                roomDimensions.utilizationColor === 'amber' ? 'text-amber-500' :
                'text-red-500'
              }`}>
                {roomDimensions.utilizationPercent}%
              </span>
            </div>
            <div className={`w-full h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  roomDimensions.utilizationColor === 'green' ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                  roomDimensions.utilizationColor === 'amber' ? 'bg-gradient-to-r from-amber-500 to-yellow-400' :
                  'bg-gradient-to-r from-red-500 to-rose-400'
                }`}
                style={{ width: `${Math.min(roomDimensions.utilizationPercent, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className={`text-xs ${subtext}`}>
                {roomDimensions.utilizationStatus === 'good' && 'Well-distributed'}
                {roomDimensions.utilizationStatus === 'tight' && 'Tight fit'}
                {roomDimensions.utilizationStatus === 'too-small' && 'Rooms too small'}
              </span>
              <span className={`text-xs ${subtext}`}>
                {roomDimensions.hallwayArea} {config.unit} for hallways
              </span>
            </div>
          </div>

          {/* Room Grid */}
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {roomDimensions.rooms.map((room: any, idx: number) => (
              <div
                key={idx}
                className={`p-3 rounded-lg border ${
                  darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <i className={`text-sm ${
                    room.type === 'living' ? 'ri-sofa-line text-teal-500' :
                    room.type === 'bedroom' ? 'ri-hotel-bed-line text-indigo-500' :
                    room.type === 'bathroom' ? 'ri-water-flash-line text-cyan-500' :
                    'ri-restaurant-line text-purple-500'
                  }`}></i>
                  <span className={`text-xs font-bold ${text}`}>{room.name}</span>
                </div>
                <div className={`text-lg font-bold ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                  {room.dimensions}
                </div>
                <div className={`text-xs ${subtext}`}>
                  {room.areaDisplay} {config.unit}
                </div>
              </div>
            ))}
          </div>

          {/* Recalculate Button */}
          <button
            onClick={calculateOptimalRooms}
            className={`w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer ${
              darkMode
                ? 'bg-indigo-800 text-indigo-200 hover:bg-indigo-700'
                : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            }`}
          >
            <i className="ri-refresh-line"></i>
            Recalculate
          </button>
        </div>
      )}

      {/* Kitchen Type */}
      <div>
        <label className={`block text-sm font-semibold mb-3 ${text} flex items-center gap-2`}>
          <i className="ri-restaurant-line text-teal-500"></i>
          Kitchen Type
        </label>
        <div className="grid grid-cols-2 gap-2">
          {KITCHEN_TYPES.map((type) => (
            <button
              key={type.value}
              onClick={() => onChange({ ...config, kitchenType: type.value })}
              className={`p-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                config.kitchenType === type.value
                  ? darkMode
                    ? 'border-teal-500 bg-teal-900/30'
                    : 'border-teal-500 bg-teal-50'
                  : darkMode
                  ? 'border-gray-600 bg-gray-700/60 hover:border-gray-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <i className={`${type.icon} text-sm ${config.kitchenType === type.value ? 'text-teal-500' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                <span className={`text-xs font-bold ${config.kitchenType === type.value ? darkMode ? 'text-teal-300' : 'text-teal-700' : text}`}>{type.label}</span>
              </div>
              <div className={`text-xs ${subtext} leading-tight`}>{type.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Extras */}
      <div>
        <label className={`block text-sm font-semibold mb-3 ${text} flex items-center gap-2`}>
          <i className="ri-add-circle-line text-teal-500"></i>
          Additional Features
          {config.extras.length > 0 && (
            <span className="ml-auto px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full font-bold">{config.extras.length}</span>
          )}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {EXTRAS.map((extra) => {
            const selected = config.extras.includes(extra.label);
            return (
              <button
                key={extra.label}
                onClick={() => {
                  const newExtras = selected
                    ? config.extras.filter((e: string) => e !== extra.label)
                    : [...config.extras, extra.label];
                  onChange({ ...config, extras: newExtras });
                }}
                className={`flex items-center gap-2 py-2.5 px-3 rounded-xl border text-sm font-medium transition-all duration-200 cursor-pointer ${
                  selected
                    ? darkMode
                      ? 'border-teal-500 bg-teal-900/30 text-teal-300'
                      : 'border-teal-500 bg-teal-50 text-teal-700'
                    : darkMode
                    ? 'border-gray-600 bg-gray-700/60 text-gray-300 hover:border-gray-500'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${extra.icon} text-sm ${selected ? 'text-teal-500' : darkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
                <span className="text-xs">{extra.label}</span>
                {selected && <i className="ri-check-line text-teal-500 text-xs ml-auto"></i>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Reset + Generate */}
      <div className="space-y-3 pt-1">
        {onReset && (
          <button
            onClick={onReset}
            className={`w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap border ${
              darkMode
                ? 'border-gray-600 text-gray-400 hover:text-red-400 hover:border-red-700 hover:bg-red-900/20'
                : 'border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-300 hover:bg-red-50'
            }`}
          >
            <i className="ri-restart-line"></i>
            Reset to Default
          </button>
        )}

        <button
          onClick={onGenerate}
          disabled={isGenerating}
          className={`w-full py-4 rounded-xl font-bold text-base transition-all duration-300 whitespace-nowrap flex items-center justify-center gap-3 relative overflow-hidden ${
            isGenerating
              ? 'bg-gray-400 cursor-not-allowed text-white'
              : 'bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-600 hover:from-teal-600 hover:via-cyan-600 hover:to-teal-700 text-white cursor-pointer shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 hover:-translate-y-0.5'
          }`}
        >
          {isGenerating ? (
            <>
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              Generating Design...
            </>
          ) : (
            <>
              <i className="ri-magic-line text-xl"></i>
              generate design visuals: Floor plan + 3D render
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-sm font-semibold">
                {totalRoomCount} rooms
              </span>
              <i className="ri-arrow-right-line text-lg"></i>
            </>
          )}
          {!isGenerating && (
            <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
          )}
        </button>
      </div>
    </div>
  );
}